import { z } from "zod";
import { GOVERNANCE_RULES, type GovernanceRule, type RuleTag } from "./governanceRules";

// ---- AI analysis of a works permit ("tenant request") ----
//
// The AI reads the association's regulations and the request (with its supporting
// documents) and proposes a decision, PUBLISHING its reasoning and the exact
// articles/sections it relied on. Powered by Claude when ANTHROPIC_API_KEY is set;
// otherwise a deterministic rules-engine fallback keeps the feature usable.

export const aiRecommendationEnum = z.enum([
  "APPROVE",
  "APPROVE_WITH_CONDITIONS",
  "REJECT",
  "NEEDS_MORE_INFO",
]);
export type AiRecommendation = z.infer<typeof aiRecommendationEnum>;

// Tolerant sub-schemas: LLM output occasionally omits a field or returns a slightly
// off value — default/`.catch` those to safe values so the analysis still parses
// instead of collapsing to the rules-engine fallback over a minor imperfection.
const reasoningItemSchema = z.object({
  titleEs: z.string().catch("").default(""),
  detailEs: z.string().catch("").default(""),
});
const citationItemSchema = z.object({
  source: z.string().catch("").default(""), // e.g. "Manual de Normas", "Reglamento"
  article: z.string().catch("").default(""), // e.g. "§7.2", "Art. VI Sec. 2(T)"
  relevance: z.string().catch("").default(""), // why it applies to this request
});

// The shape the model is asked to return (validated + repaired server-side).
export const worksPermitAnalysisSchema = z.object({
  recommendation: aiRecommendationEnum.catch("NEEDS_MORE_INFO"),
  summaryEs: z.string().min(1),
  reasoning: z.array(reasoningItemSchema).catch([]).default([]),
  citations: z.array(citationItemSchema).catch([]).default([]),
  conditions: z.array(z.string()).catch([]).default([]),
  confidence: z.enum(["low", "medium", "high"]).optional().catch(undefined),

  // A specific rule forbids this work outright — distinct from `recommendation: "REJECT"`,
  // which is the board-facing judgement call ("decline on balance"). Deliberately a
  // separate boolean rather than a fifth recommendation value: `recommendation` carries
  // `.catch("NEEDS_MORE_INFO")`, so an unrecognised enum value would silently erase the
  // prohibition — the one failure this feature cannot afford.
  //
  // `prohibitedQuoteEs` is the verbatim sentence from the governing document. The server
  // checks that sentence really appears in that document's extracted text before raising a
  // flag, so a hallucinated citation can never reach a resident (see verifyProhibition).
  prohibited: z.boolean().catch(false).default(false),
  prohibitedReasonEs: z.string().catch("").default(""),
  prohibitedQuoteEs: z.string().catch("").default(""),
  prohibitedSourceEs: z.string().catch("").default(""),
});
export type WorksPermitAnalysisCore = z.infer<typeof worksPermitAnalysisSchema>;

// What we persist on the permit (core result + provenance).
export interface WorksPermitAnalysis extends WorksPermitAnalysisCore {
  engine: "claude" | "rules-engine";
  model?: string;
  documentsRead: string[]; // titles of regulation docs actually read by the AI
  disclaimerEs: string;
  generatedAt: string; // ISO timestamp
}

export const AI_DISCLAIMER_ES =
  "Este análisis es una recomendación generada por inteligencia artificial como apoyo a la " +
  "decisión de la Junta. No sustituye el criterio de la Junta de Directores ni constituye una " +
  "decisión oficial; verifique las citas contra los documentos originales.";

export const recommendationLabel: Record<AiRecommendation, { es: string; en: string }> = {
  APPROVE: { es: "Recomendado: Aprobar", en: "Recommended: Approve" },
  APPROVE_WITH_CONDITIONS: { es: "Aprobar con condiciones", en: "Approve with conditions" },
  REJECT: { es: "Recomendado: Rechazar", en: "Recommended: Reject" },
  NEEDS_MORE_INFO: { es: "Requiere más información", en: "Needs more information" },
};

// Normalize (lowercase, strip accents) so keyword matching is tolerant.
function norm(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s]/g, " ");
}

/** Governance rules whose keywords appear in the given text, plus any tagged with baseTags. */
export function relevantRules(
  text: string,
  baseTags: RuleTag[] = ["works-permit", "architectural"]
): GovernanceRule[] {
  const hay = norm(text);
  const out: GovernanceRule[] = [];
  for (const rule of GOVERNANCE_RULES) {
    if (rule.keywords.some((k) => hay.includes(norm(k)))) out.push(rule);
  }
  for (const rule of GOVERNANCE_RULES) {
    if (!out.includes(rule) && rule.tags.some((t) => baseTags.includes(t))) out.push(rule);
  }
  return out;
}

// ---- AI analysis of a maintenance request (triage: responsibility + priority) ----

export const maintenanceResponsibilityEnum = z.enum(["ASSOCIATION", "OWNER", "SHARED", "UNCLEAR"]);
export type MaintenanceResponsibility = z.infer<typeof maintenanceResponsibilityEnum>;

export const maintenanceAnalysisSchema = z.object({
  responsibility: maintenanceResponsibilityEnum.catch("UNCLEAR"),
  suggestedPriority: z.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]).catch("MEDIUM"),
  summaryEs: z.string().min(1),
  reasoning: z.array(reasoningItemSchema).catch([]).default([]),
  citations: z.array(citationItemSchema).catch([]).default([]),
  recommendedActions: z.array(z.string()).catch([]).default([]),
  confidence: z.enum(["low", "medium", "high"]).optional().catch(undefined),
});
export type MaintenanceAnalysisCore = z.infer<typeof maintenanceAnalysisSchema>;

export interface MaintenanceAnalysis extends MaintenanceAnalysisCore {
  engine: "claude" | "rules-engine";
  model?: string;
  documentsRead: string[];
  disclaimerEs: string;
  generatedAt: string;
}

export const responsibilityLabel: Record<MaintenanceResponsibility, { es: string; en: string }> = {
  ASSOCIATION: { es: "Responsabilidad: Condominio (áreas comunes)", en: "Responsibility: Association (common areas)" },
  OWNER: { es: "Responsabilidad: Titular / unidad", en: "Responsibility: Owner / unit" },
  SHARED: { es: "Responsabilidad: Compartida", en: "Responsibility: Shared" },
  UNCLEAR: { es: "Requiere inspección", en: "Needs inspection" },
};

export const aiPriorityLabel: Record<"LOW" | "MEDIUM" | "HIGH" | "URGENT", { es: string; en: string }> = {
  LOW: { es: "Prioridad baja", en: "Low priority" },
  MEDIUM: { es: "Prioridad media", en: "Medium priority" },
  HIGH: { es: "Prioridad alta", en: "High priority" },
  URGENT: { es: "Urgente", en: "Urgent" },
};

export function relevantRuleTags(text: string): RuleTag[] {
  const tags = new Set<RuleTag>();
  for (const r of relevantRules(text)) r.tags.forEach((t) => tags.add(t));
  return [...tags];
}

// ---- Impartial AI evaluation of the Junta's DECISION (arbitrariness audit) ----
//
// Distinct from the request analysis above: this audits the board's decision +
// conditions, classifying each condition it imposed as required by a rule (with
// citation), discretionary but reasonable, or unsupported/arbitrary.

export const decisionVerdictEnum = z.enum([
  "WELL_GROUNDED",
  "MOSTLY_GROUNDED",
  "SOME_CONCERNS",
  "ARBITRARY_ELEMENTS",
]);
export type DecisionVerdict = z.infer<typeof decisionVerdictEnum>;

export const conditionBasisEnum = z.enum(["RULE_BASED", "DISCRETIONARY", "UNSUPPORTED"]);
export type ConditionBasis = z.infer<typeof conditionBasisEnum>;

const evaluatedConditionSchema = z.object({
  conditionEs: z.string().catch("").default(""), // the board's condition, restated
  basis: conditionBasisEnum.catch("DISCRETIONARY"),
  citation: citationItemSchema.optional(), // source + article when RULE_BASED
  assessmentEs: z.string().catch("").default(""), // why grounded / discretionary / arbitrary
});

export const decisionEvaluationSchema = z.object({
  verdict: decisionVerdictEnum.catch("SOME_CONCERNS"),
  summaryEs: z.string().min(1),
  conditions: z.array(evaluatedConditionSchema).catch([]).default([]),
  concerns: z.array(z.string()).catch([]).default([]), // flagged arbitrary / unsupported items
  proceduralNotesEs: z.string().catch("").default(""), // quorum / abstention / notice observations
  confidence: z.enum(["low", "medium", "high"]).optional().catch(undefined),
});
export type DecisionEvaluationCore = z.infer<typeof decisionEvaluationSchema>;

export interface DecisionEvaluation extends DecisionEvaluationCore {
  engine: "claude" | "rules-engine";
  model?: string;
  documentsRead: string[];
  disclaimerEs: string;
  generatedAt: string;
}

// Request body for POST /works-permits/:id/evaluate-decision.
export const evaluateDecisionSchema = z.object({
  additionalDocUrls: z.array(z.string().url()).optional().default([]),
});

export const DECISION_AUDIT_DISCLAIMER_ES =
  "Evaluación imparcial generada por inteligencia artificial para verificar que la decisión de la " +
  "Junta esté fundamentada en los reglamentos. No sustituye el criterio de la Junta ni de los " +
  "titulares; verifique las citas contra los documentos originales.";

export const verdictLabel: Record<DecisionVerdict, { es: string; en: string }> = {
  WELL_GROUNDED: { es: "Bien fundamentada", en: "Well grounded" },
  MOSTLY_GROUNDED: { es: "Mayormente fundamentada", en: "Mostly grounded" },
  SOME_CONCERNS: { es: "Con observaciones", en: "Some concerns" },
  ARBITRARY_ELEMENTS: { es: "Elementos arbitrarios", en: "Arbitrary elements" },
};

export const basisLabel: Record<ConditionBasis, { es: string; en: string }> = {
  RULE_BASED: { es: "Exigido por regla", en: "Required by a rule" },
  DISCRETIONARY: { es: "Discrecional", en: "Discretionary" },
  UNSUPPORTED: { es: "Sin base / arbitrario", en: "Unsupported / arbitrary" },
};
